import { ReactNode, useLayoutEffect } from 'react'
import { useLatest } from 'react-use'
import { Vector3 } from 'three'
import { v4 } from 'uuid'
import { SceneContextMenuAction, SceneContextMenuActionRenderProps } from '@ui/scene/Context/types'
import { useControlStore } from '@editorStores'

/**
 * All properties should be memoed to avoid retriggering the effect on every render.
 * @param param0
 */
const useSceneContextMenuAction = ({
  label,
  action,
  available,
  render,
  group,
}: {
  label?: string
  action?: (
    selectedElement: string | null,
    point: Vector3,
    event: React.MouseEvent<Element, MouseEvent>,
    userDataActions: Record<string, unknown>,
    userData: Record<string, unknown>,
  ) => void
  available?: (
    selectedElement: string | null,
    point: Vector3 | null,
    userDataActions: Record<string, unknown>,
    userData: Record<string, unknown>,
  ) => boolean
  // Render a custom component into the context menu (e.g. an input field)
  // instead of a clickable label. The context menu stays agnostic about it.
  render?: (props: SceneContextMenuActionRenderProps) => ReactNode
  group?: string
}) => {
  const addSceneContextMenuAction = useControlStore(state => state.addSceneContextMenuAction)
  const removeSceneContextMenuAction = useControlStore(state => state.removeSceneContextMenuAction)

  const actionCallback = useLatest(action)
  const availableCallback = useLatest(available)

  useLayoutEffect(() => {
    const id = v4()
    const newAction: SceneContextMenuAction = {
      id,
      label,
      action: actionCallback.current,
      available: availableCallback.current,
      render,
      group: group || 'no-group',
    }

    addSceneContextMenuAction(newAction)

    return () => {
      removeSceneContextMenuAction(newAction)
    }
  }, [
    actionCallback,
    availableCallback,
    render,
    addSceneContextMenuAction,
    group,
    label,
    removeSceneContextMenuAction,
  ])
}

export default useSceneContextMenuAction
